'use client';
import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import ConsentText from '@/components/public/ConsentText';
import { createClient } from '@/lib/supabase/client';
import { subscribeToUpdates } from '@/lib/email/emailService';
import { withUtmSource } from '@/lib/utm';

const STRIPE_LINK = 'https://buy.stripe.com/14A3cuexdfSX5EA80UbEA05';

interface SignupModalProps {
  open: boolean;
  onClose: () => void;
  source: string;
}

type FormErrors = { name?: string; email?: string; consent?: string; waiver?: string };

export default function SignupModal({ open, onClose, source }: SignupModalProps) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [waiver, setWaiver] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [website, setWebsite] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    const next: FormErrors = {};
    if (!fullName.trim()) next.name = 'Enter your full name';
    if (!email.trim()) next.email = 'Enter your email address';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = 'Enter a valid email address';
    if (!consent) next.consent = 'Please tick the box so we can contact you';
    if (!waiver) next.waiver = 'Please tick this box to continue';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    const cleanEmail = email.trim().toLowerCase();
    const paymentUrl = `${STRIPE_LINK}?prefilled_email=${encodeURIComponent(cleanEmail)}`;

    // Honeypot: skip the save but still continue to payment
    if (website.trim() === '') {
      try {
        const supabase = createClient();
        await supabase.from('membership_interest').insert({
          full_name: fullName.trim(),
          email: cleanEmail,
          phone: phone.trim() || null,
          interested_in: 'Course 1 (£49)',
          business_stage: 'Start page',
          source: withUtmSource(source),
          consent_to_contact: true,
          message: marketing ? 'marketing: yes' : null,
        });
      } catch {
        // Saving failed: still continue to payment
      }

      if (marketing) {
        try {
          await subscribeToUpdates({
            email: cleanEmail,
            fullName: fullName.trim().split(/\s+/)[0] || fullName.trim(),
            source: 'start-page',
          });
        } catch {
          // Subscribing failed: still continue to payment
        }
      }
    }

    window.location.href = paymentUrl;
  };

  return (
    <Modal open={open} onClose={onClose} title="Start Course 1 for £49">
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <div
          aria-hidden="true"
          style={{ position: 'absolute', left: '-10000px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}
        >
          <label htmlFor="start-website">Website</label>
          <input
            id="start-website"
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="start-name" className="mb-1.5 block text-sm font-semibold text-foreground">Full name</label>
          <input
            id="start-name"
            type="text"
            autoComplete="name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className={`field-input !py-3 !text-base ${errors.name ? 'field-input-error' : ''}`}
          />
          {errors.name && <p className="mt-1.5 text-xs font-medium text-destructive">{errors.name}</p>}
        </div>
        <div>
          <label htmlFor="start-email" className="mb-1.5 block text-sm font-semibold text-foreground">Email</label>
          <input
            id="start-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`field-input !py-3 !text-base ${errors.email ? 'field-input-error' : ''}`}
          />
          <p className="mt-1.5 text-xs text-muted-foreground">Use the email you want to log in with</p>
          {errors.email && <p className="mt-1.5 text-xs font-medium text-destructive">{errors.email}</p>}
        </div>
        <div>
          <label htmlFor="start-phone" className="mb-1.5 block text-sm font-semibold text-foreground">Phone (optional)</label>
          <input
            id="start-phone"
            type="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="field-input !py-3 !text-base"
          />
        </div>
        <div>
          <label htmlFor="start-consent" className="flex cursor-pointer items-start gap-3 text-sm text-foreground">
            <input
              id="start-consent"
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 h-5 w-5 flex-shrink-0 rounded border-input accent-[#581C32]"
            />
            <span>I agree to Care Business School contacting me about my course.</span>
          </label>
          {errors.consent && <p className="mt-1.5 text-xs font-medium text-destructive">{errors.consent}</p>}
        </div>
        <div>
          <label htmlFor="start-waiver" className="flex cursor-pointer items-start gap-3 text-sm text-foreground">
            <input
              id="start-waiver"
              type="checkbox"
              checked={waiver}
              onChange={(e) => setWaiver(e.target.checked)}
              aria-required="true"
              className="mt-0.5 h-5 w-5 flex-shrink-0 rounded border-input accent-[#581C32]"
            />
            <span>I understand I&apos;ll get immediate access to Course 1, and that I lose my right to cancel once I start the course.</span>
          </label>
          {errors.waiver && <p className="mt-1.5 text-xs font-medium text-destructive">{errors.waiver}</p>}
        </div>
        <div>
          <label htmlFor="start-marketing" className="flex cursor-pointer items-start gap-3 text-sm text-foreground">
            <input
              id="start-marketing"
              type="checkbox"
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
              className="mt-0.5 h-5 w-5 flex-shrink-0 rounded border-input accent-[#581C32]"
            />
            <span>Send me tips, updates and offers from Care Business School by email.</span>
          </label>
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex min-h-[56px] w-full items-center justify-center gap-2 rounded-xl bg-[#C6A667] px-6 py-4 text-base font-bold text-[#2B0D18] shadow-md transition-all hover:brightness-105 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? (<><Loader2 size={18} className="animate-spin" /> Taking you to payment…</>) : 'Continue to secure payment'}
        </button>
        <ConsentText className="text-center text-muted-foreground" />
        <p className="text-center text-xs text-muted-foreground">
          Secure payment by Stripe. You&apos;ll get your login invitation by email straight after payment.
        </p>
      </form>
    </Modal>
  );
}
